import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { PhoneAgent } from '../../components/PhoneAgent';
import { useCountUp } from '../../hooks';

/** Headline segments rise one after another on load. */
interface Segment {
  text: string;
  mark?: boolean;
}
const SEGMENTS: Segment[] = [
  { text: 'We' },
  { text: 'build' },
  { text: 'AI agents', mark: true },
  { text: 'and' },
  { text: 'the' },
  { text: 'software' },
  { text: 'they' },
  { text: 'run' },
  { text: 'on.' },
];
const STEP = 0.055;
const START = 0.06;
const AFTER = START + SEGMENTS.length * STEP;

interface StatProps {
  target: number;
  prefix?: string;
  suffix?: string;
  label: string;
}

function HeroStat({ target, prefix, suffix, label }: StatProps): React.JSX.Element {
  const { ref, value } = useCountUp(target);
  return (
    <div className="hero-stat">
      <span className="hero-stat-value" ref={ref}>
        {prefix}
        {value}
        {suffix}
      </span>
      <span className="hero-stat-label">{label}</span>
    </div>
  );
}

export function Hero(): React.JSX.Element {
  return (
    <section className="hero" id="top">
      <div className="hero-graph" aria-hidden="true" />
      <div className="blob blob-blue" style={{ width: 620, height: 620, top: -220, right: '-6%' }} />
      <div className="blob blob-violet" style={{ width: 380, height: 380, bottom: -80, left: '-4%' }} />

      <div className="container">
        <div className="hero-grid">
          <div className="hero-copy">
            <h1 className="h1 hero-title">
              {SEGMENTS.map((seg, i) => (
                <Fragment key={seg.text}>
                  <span
                    className="hero-word"
                    style={{ animationDelay: `${START + i * STEP}s` }}
                  >
                    {seg.mark ? <span className="mark">{seg.text}</span> : seg.text}
                  </span>{' '}
                </Fragment>
              ))}
            </h1>

            <p className="lede hero-fade" style={{ animationDelay: `${AFTER + 0.1}s` }}>
              Agents that answer, decide, and act — grounded in your documents with RAG
              and wired into your systems. Plus the ERPs, portals, and platforms they
              plug into, from a team with 7+ years of international experience.
            </p>

            <div className="hero-ctas hero-fade" style={{ animationDelay: `${AFTER + 0.18}s` }}>
              <Link to="/#contact" className="btn btn-primary btn-lg">
                Start a project <span className="arrow">→</span>
              </Link>
              <Link to="/#work" className="btn btn-ghost btn-lg">
                See our work
              </Link>
            </div>

            <div className="hero-stats hero-fade" style={{ animationDelay: `${AFTER + 0.26}s` }}>
              <HeroStat target={7} suffix="+" label="Years building software" />
              <HeroStat target={24} suffix="/7" label="Agents on shift" />
            </div>
          </div>

          <div className="hero-stage hero-fade" style={{ animationDelay: `${AFTER + 0.34}s` }}>
            <PhoneAgent />
          </div>
        </div>
      </div>
    </section>
  );
}
